import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { unwrap } from '@/api/envelope';
import {
  listArcContent,
  listPages,
  readPageActivistArcOrganizationPortalPagesPageIdGet as readPage,
} from '@/api/generated';
import { useAuth } from '@/auth/useAuth';
import { useViewingAs } from '@/auth/useViewingAs';

import {
  pageDetailKey,
  pageDocumentsKey,
  pageLimitsKey,
  pagesListKey,
  pagesRoot,
} from './keys';
import {
  toPageDetail,
  toPageLimits,
  toPageRow,
  type PageDetail,
  type PageLimits,
  type PageRow,
} from './pageLabels';
import type { PagesParams } from './usePagesParams';

/** Cards per page: the backend's default, and what the grid holds (PNF1). */
export const PAGE_SIZE = 20;

/** Documents per page of the picker (PNF3). */
export const CHOICES_PAGE_SIZE = 20;

/**
 * Which organization's pages are shown: the one staff are viewing, or the
 * signed-in person's own. Every key carries it (see keys.ts).
 */
export function usePagesOrganizationId(): string | null {
  const { user } = useAuth();
  const { viewing } = useViewingAs();
  return viewing ? viewing.id : (user?.organizationId ?? null);
}

/**
 * Asks again for everything the organization's pages show, after a write:
 * the list, an open editor, the picker and the limits, and nothing of any
 * other organization's or section's (I7, PNF6). The ARC's keys are the
 * caller's, where P24 says.
 */
export function useInvalidatePages(): () => Promise<void> {
  const queryClient = useQueryClient();
  const organizationId = usePagesOrganizationId();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: pagesRoot(organizationId) }),
    [queryClient, organizationId],
  );
}

export interface PagesPage {
  rows: PageRow[];
  pagination: { total: number; page: number; limit: number; hasMore: boolean };
}

/**
 * The request for a view, with the defaults left out as the backend's own.
 * No range: the views are the backend's 30 days (P9).
 */
export function listQueryFor(params: PagesParams) {
  const search = params.search.trim();
  return {
    page: params.page,
    limit: PAGE_SIZE,
    ...(search ? { search } : {}),
    ...(params.status ? { status: params.status } : {}),
    ...(params.placement ? { arc_placement: params.placement } : {}),
  };
}

/**
 * One page of the list for a view. The previous page stays on screen while
 * the next loads, but only within one organization: after a switch nothing
 * of the first organization's is drawn under the second's name (PNF6).
 */
export function usePagesList(params: PagesParams) {
  const organizationId = usePagesOrganizationId();
  return useQuery({
    queryKey: pagesListKey(organizationId, params),
    enabled: organizationId !== null,
    queryFn: async (): Promise<PagesPage> => {
      const reply = await listPages({ query: listQueryFor(params), throwOnError: true });
      const data = unwrap(reply.data);
      return {
        rows: data.items.map(toPageRow),
        pagination: {
          total: data.pagination.total,
          page: data.pagination.page,
          limit: data.pagination.limit,
          hasMore: data.pagination.has_more,
        },
      };
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === organizationId
        ? keepPreviousData(previous)
        : undefined,
  });
}

async function fetchPage(id: string): Promise<PageDetail> {
  const reply = await readPage({ path: { page_id: id }, throwOnError: true });
  return toPageDetail(unwrap(reply.data));
}

/** One page with its content, links and limits (B12); another organization's is a 404 (B13). */
export function usePageDetail(id: string | null) {
  const organizationId = usePagesOrganizationId();
  return useQuery({
    queryKey: pageDetailKey(organizationId, id ?? ''),
    enabled: id !== null && organizationId !== null,
    queryFn: () => fetchPage(id as string),
  });
}

/**
 * Reads one page afresh, on demand: the editor's Reload after a 409 (P17).
 * The open editor keeps no watch on the detail, so a write's invalidation
 * does not ask for a page the form already holds from the write's reply.
 */
export function useReadPage(): (id: string) => Promise<PageDetail> {
  const queryClient = useQueryClient();
  const organizationId = usePagesOrganizationId();
  return useCallback(
    (id: string) =>
      queryClient.fetchQuery({
        queryKey: pageDetailKey(organizationId, id),
        queryFn: () => fetchPage(id),
        staleTime: 0,
      }),
    [queryClient, organizationId],
  );
}

/**
 * The limits a new page is drawn against, read from the list reply under a
 * key of its own, so it never shares the list's cache entry (P3). Off for a
 * saved page, whose limits come with it.
 */
export function usePageLimits(enabled = true) {
  const organizationId = usePagesOrganizationId();
  return useQuery({
    queryKey: pageLimitsKey(organizationId),
    enabled: enabled && organizationId !== null,
    queryFn: async (): Promise<PageLimits> => {
      const reply = await listPages({ query: { limit: 1 }, throwOnError: true });
      return toPageLimits(unwrap(reply.data).limits);
    },
  });
}

/**
 * One page of the organization's published documents, for the Linked
 * Content picker (`GET /arc/content`, P14). `q` is the settled search: the
 * caller debounces it. The feature's own hook, so the Pages section does not
 * reach into the ARC's.
 */
export function useDocumentChoices(q: string, page: number) {
  const organizationId = usePagesOrganizationId();
  return useQuery({
    queryKey: pageDocumentsKey(organizationId, q, page),
    enabled: organizationId !== null,
    queryFn: async () => {
      const reply = await listArcContent({
        query: { type: 'DOCUMENT', q: q || undefined, page, limit: CHOICES_PAGE_SIZE },
        throwOnError: true,
      });
      return unwrap(reply.data);
    },
    staleTime: 60_000,
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === organizationId ? previous : undefined,
  });
}
